import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { SubscriptionData, UpdateSubscriptionResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
// import { useNavigate } from "react-router-dom"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"

const Subscriptions: React.FC = () => {
  // const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalItems, setTotalItems] = useState(100)
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 500)
  const [sort, setSort] = useState("DESC")
  const [selectedRows, setSelectedRows] = React.useState<SubscriptionData[]>([])
  const [sortCol, setSortCol] = useState("createdAt")

  const fetchSubscriptions = useCallback(async () => {
    try {
      dispatch(changeloaderState(true))
      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText,
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<UpdateSubscriptionResponse>({
        url: "/adminCustomers/allOwners",
        method: "POST",
        data: params,
      })

      if (response.data) {
        setSubscriptions(response.data)
        setTotalItems(response.total)
      } else {
        toast.error("Failed to fetch: " + response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    } finally {
      dispatch(changeloaderState(false))
    }
  }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol])

  useEffect(() => {
    fetchSubscriptions()
  }, [page, rowsPerPage, debounceText, sort, fetchSubscriptions])

  useEffect(() => {
    if (debounceText) {
      setPage(1) // Reset to first page on search
    }
  }, [debounceText])

  const addAction = {
    label: selectedRows.every((row) => row.userAccounts?.subscription_status === true)
      ? "Pause"
      : "Start",
    type: "button",
    onClick: async () => {
      const userIds = selectedRows.map((row) => row.userAccounts?.id)

      const allSubscribed = selectedRows.every(
        (row) => row.userAccounts?.subscription_status === true,
      )

      const payload = {
        userIds,
        status: allSubscribed ? 0 : 1,
      }
      try {
        const response = await request({
          url: `/adminCustomers/updateSubscriptionStatus`,
          method: "POST",
          data: payload,
        })
        if (response.data) {
          toast.success(response.message)
          setSelectedRows([])
          fetchSubscriptions()
        } else {
          toast.error(response.message)
          return
        }
      } catch (error: any) {
        toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        return
      }
    },
    className: `resetBtn ${selectedRows.length <= 0 && "btn btn-secondary"}`,
    disabled: selectedRows.length <= 0,
  }

  const handleSubscriptionStatusUpdate = async (row: any) => {
    const payload = {
      userIds: [row?.userAccounts?.id],
      status: row?.userAccounts?.subscription_status ? "0" : " 1",
    }
    try {
      const response = await request({
        url: `/adminCustomers/updateSubscriptionStatus`,
        method: "POST",
        data: payload,
      })
      if (response.data) {
        toast.success(response.message)
        fetchSubscriptions()
      } else {
        toast.error(response.message)
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
    }
  }

  const columns = [
    {
      name: "Name",
      value: (subscription: SubscriptionData) =>
        !subscription.first_name && !subscription.last_name
          ? "-"
          : (subscription.first_name ? subscription.first_name : "") +
          " " +
          (subscription.last_name ? subscription.last_name : ""),

      id: "first_name",
      className: "long-text-devices"
    },
    {
      name: "Type",
      value: (subscription: SubscriptionData) =>
        subscription.is_police === true ? "Police" : " User",
      id: "Type",
      // className: "long-text-devices"
    },
    {
      name: "SOS Status",
      value: (subscription: SubscriptionData) =>
        subscription?.SOS_Status === "0" ? "Active" : "Inactive",
      id: "SOS_Status",
      className: "long-text-devices"
    },
    {
      name: "Sensor Status",
      value: (subscription: SubscriptionData) =>
        subscription?.Sensor_Status === "0" ? "Active" : "Inactive",
      id: "Sensor_Status",
    },
    {
      name: "Added payment details?",
      value: (subscription: SubscriptionData) =>
        subscription?.userAccounts?.paymentUpdated === 0 ? "No" : "Yes",
      id: "Sensor_Status",
    },
    {
      name: "Next Billing SOS",
      value: (subscription: SubscriptionData) => subscription?.NextBillSOS || "-",
      id: "NextBillSOS",
    },
    {
      name: "Next Billing Sensor",
      value: (subscription: SubscriptionData) => subscription?.userAccounts?.NextBillSensor || "-",
      id: "NextBillSensor",
    },
    {
      name: "Subscription Status",
      value: (subscription: SubscriptionData) => (
        <PrimaryButton
          label={subscription?.userAccounts?.subscription_status === true ? "Started" : "Stopped"}
          className='subscription-status-btn m-0'
          onClick={() => handleSubscriptionStatusUpdate(subscription)}
        />
      ),
      id: "subscription_status",
    },
  ]

  const action: TableAction[] = [
    {
      label: "Bills",
      // onClick: (row: SubscriptionData) => {
      //   navigate(`/customer/bills/${row?.userId}`)
      // },
      isRedirect: true,
      link: (row: SubscriptionData) => `/customer/bills/${row?.userId}`,
      className: "table-btn-1",
    },
  ]

  const fetchAllSubscriptions = async () => {
    try {
      const params = {
        page: 1,
        search: debounceText,
        limit: 100000,
        sort: sort,
        sortCol: sortCol,
      }

      const response = await request<UpdateSubscriptionResponse>({
        url: "/adminCustomers/allOwners",
        method: "POST",
        data: params,
      })

      if (response.data) {
        return response.data
      } else {
        toast.error("Failed to fetch all data: " + response.message)
        return []
      }
    } catch (error: any) {
      toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
      return []
    }
  }


  const exportCSV = async () => {

    const allData = await fetchAllSubscriptions()

    if (!allData.length) {
      toast.error("No data available to export")
      return
    }

    const headers = [
      "Sr. No.",
      "Name",
      "Type",
      "SOS Status",
      "Sensor Status",
      "Payment Added?",
      "Next Billing SOS",
      "Next Billing Sensor",
      "Subscription Status",
    ]

    const rows = allData.map((s, index) => [
      index + 1,
      (!s.first_name && !s.last_name) ? "-" : `${s.first_name || ""} ${s.last_name || ""}`,
      s.is_police ? "Police" : "User",
      s.SOS_Status === "1" ? "Active" : "Inactive",
      s.Sensor_Status === "1" ? "Active" : "Inactive",
      s.userAccounts?.paymentUpdated === 0 ? "No" : "Yes",
      s.userAccounts?.NextBillSOS || "-",
      s.userAccounts?.NextBillSensor || "-",
      s.userAccounts?.subscription_status ? "Started" : "Stopped",
    ])

    donwloadCSV(headers, rows, "Subscriptions.csv")
  }

  const exportPDF = async () => {

    const allData = await fetchAllSubscriptions()

    if (!allData.length) {
      toast.error("No data available to export")
      return
    }

    const headers = [
      "Sr. No.",
      "Name",
      "Type",
      "SOS Status",
      "Sensor Status",
      "Payment Added?",
      "Next Billing SOS",
      "Next Billing Sensor",
      "Subscription Status",
    ]

    const rows = allData.map((s, index) => [
      index + 1,
      (!s.first_name && !s.last_name) ? "-" : `${s.first_name || ""} ${s.last_name || ""}`,
      s.is_police ? "Police" : "User",
      s.SOS_Status === "1" ? "Active" : "Inactive",
      s.Sensor_Status === "1" ? "Active" : "Inactive",
      s.userAccounts?.paymentUpdated === 0 ? "No" : "Yes",
      s.userAccounts?.NextBillSOS || "-",
      s.userAccounts?.NextBillSensor || "-",
      s.userAccounts?.subscription_status ? "Started" : "Stopped",
    ])

    // call utility
    downloadPDF(headers, rows, "Subscriptions")
  }

  return (
    <section className=' dashboard-section dashboard-item '>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='dashboard-table-title-section'>
              <h2>Subscriptions</h2>
              <div className='dashboard-table-title-right-side d-flex gap-2'>
                <PrimaryButton
                  label='Export CSV'
                  className='max-w-max-content'
                  onClick={exportCSV}
                />
                <PrimaryButton
                  label='Export PDF'
                  className='max-w-max-content'
                  onClick={exportPDF}
                />
              </div>
            </div>
          </div>
          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={subscriptions}
                columns={columns}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                isSearch
                search={search}
                setSearch={setSearch}
                action={action}
                {...(selectedRows.length > 0 && { addAction })}
                setSelected={setSelectedRows}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Subscriptions
